import { constVoid } from 'fp-ts/function';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { LocalizedText } from '@mews/framework';
import { Button, DialogProps, Divider, Stack } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { AsideDialog, OptimusProvider, UnsavedChangesDialog } from '@mews-commander/ui';
import { InjectedFormProps, withHookForm } from '@mews-commander/form';
import { FormErrors, WithEnterpriseIds } from '../../../../types';
import { CREATE_RATE_ENDING_POINT, SAVE_EDIT_BUTTON } from '../../../../utilities/userFlowTestIds';
import { FormValues, WithDependentProp } from './types';
import { BaseRateFields, DependentRateFields } from './fields';
import { FieldName, WithFieldsConfig } from './fields/types';
import { RateFormBanners } from './RateFormBanners';

type RateDialogProps = Pick<DialogProps, 'active' | 'title'> &
    WithDependentProp &
    WithFieldsConfig &
    WithEnterpriseIds & {
        submitButtonTextKey: Mews.TextKey;
        selectedRateIds?: string[];
        formErrors?: FormErrors;
        isLoading?: boolean;
        unlockField?: (fieldName: FieldName) => void;
        onCancel?: () => void;
    };

type FormContentProps = WithFieldsConfig &
    WithDependentProp & {
        children: React.ReactNode;
        enterpriseCount: number;
        selectedRatesCount?: number;
    };

const FormContent = ({ children, ...rest }: FormContentProps) => (
    <Stack spacing="200" vertical>
        {rest.enterpriseCount > 1 ? <RateFormBanners {...rest} /> : null}
        {rest.selectedRatesCount && rest.selectedRatesCount > 1 ? <Divider /> : null}
        {children}
    </Stack>
);

const RateDialog = ({
    active,
    dependent,
    enterpriseIds,
    onCancel,
    submit,
    submitButtonTextKey,
    title,
    selectedRateIds,
    fieldsConfig,
    formErrors,
    isLoading,
    unlockField,
}: RateDialogProps & InjectedFormProps) => {
    const [unsaved, setUnsaved] = useState(false);
    const { reset, setError, formState } = useFormContext();
    const isCreateForm = !selectedRateIds?.length;

    useEffect(() => {
        if (formErrors?.field) {
            setError(formErrors.field, {
                message: formErrors.message,
            });
        }
    }, [formErrors, setError]);

    useEffect(() => {
        if (!active) {
            reset();
        }
    }, [active, reset]);

    const handleClose = () => {
        if (formState.isDirty) {
            setUnsaved(true);
            return;
        }

        handleDiscard();
    };

    const handleDiscard = () => {
        setUnsaved(false);
        onCancel?.();
    };

    const content = (
        <OptimusProvider density="spacious">
            <FormContent
                dependent={dependent}
                fieldsConfig={fieldsConfig}
                enterpriseCount={enterpriseIds.length}
                selectedRatesCount={selectedRateIds?.length}
            >
                {dependent ? (
                    <DependentRateFields
                        selectedRateIds={selectedRateIds}
                        enterpriseIds={enterpriseIds}
                        fieldsConfig={fieldsConfig}
                        unlockField={unlockField}
                    />
                ) : (
                    <BaseRateFields
                        selectedRateIds={selectedRateIds}
                        enterpriseIds={enterpriseIds}
                        fieldsConfig={fieldsConfig}
                        unlockField={unlockField}
                    />
                )}
            </FormContent>
        </OptimusProvider>
    );

    return (
        <>
            <AsideDialog
                footer={{
                    primary: (
                        <Button
                            data-test-id={isCreateForm ? CREATE_RATE_ENDING_POINT : SAVE_EDIT_BUTTON}
                            size="medium"
                            variant="primary"
                            onClick={submit}
                            disabled={isLoading}
                            type="submit"
                        >
                            <LocalizedText textKey={submitButtonTextKey} />
                        </Button>
                    ),
                    secondary: (
                        <Button
                            size="medium"
                            variant="tertiary"
                            onClick={handleClose}
                            disabled={isLoading}
                            type="submit"
                        >
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                }}
                active={active}
                loading={isLoading}
                onClose={isLoading ? constVoid : handleClose}
                title={title}
            >
                {content}
            </AsideDialog>
            {unsaved ? (
                <UnsavedChangesDialog onCancel={() => setUnsaved(false)} onDiscardChanges={handleDiscard} />
            ) : null}
        </>
    );
};

export const RateDialogForm = withHookForm<FormValues, RateDialogProps>()(RateDialog);
